/**
 * Round-result panel — shared shape with
 * roulette-game/src/GameMain/RoundResultPanel.js.
 *
 * Bottom-anchored sheet showing what the round paid: the winning outcome
 * (supplied by the host as `children`, the one part that forks per game), the
 * player's own stake / payout / net, and the top three winners.
 *
 * Fully controlled — it owns no timers and never decides its own lifecycle.
 * The host closes it when the next round's betting opens, which is what keeps
 * it off a live betting table.
 *
 * Replaces the two inline `.showingCard` text strips, which showed a gross
 * figure re-derived on the client and were only on screen for the 2s `finished`
 * phase. This panel is mounted at `revealing` instead — the winner is already
 * known there — with the payout marked `est.` until settlement lands.
 */

import { useEffect, useMemo } from 'react';
import '../style/resultPanel.css';
import { formatNum, formatSigned } from './resultModel';

const CONFETTI_COLORS = ['#ffdf7a', '#fff0b6', '#e91e63', '#ef4f7c', '#ff5c8d', '#fff5ee'];

/**
 * CSS-only confetti. Built once per round and unmounted with the panel, so
 * unlike ferris-game/src/sheet.js:212-228 there is no detached body node left
 * behind by a setTimeout. Seeded off `roundNumber` so StrictMode's double
 * render doesn't reshuffle the pieces mid-fall.
 */
function Confetti({ roundNumber }) {
  const pieces = useMemo(() => {
    let seed = (Number(roundNumber) || 1) * 9301 + 49297;
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    return Array.from({ length: 26 }, (_, i) => ({
      key: i,
      left: rand() * 100,
      delay: rand() * 0.4,
      duration: 1.4 + rand() * 1.2,
      size: 6 + rand() * 6,
      rotate: rand() * 360,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    }));
  }, [roundNumber]);

  return (
    <div className="rr-confetti" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.key}
          style={{
            left: `${p.left}vw`,
            width: `${p.size}px`,
            height: `${p.size * 1.6}px`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            transform: `rotate(${p.rotate}deg)`,
          }}
        />
      ))}
    </div>
  );
}

function PodiumCard({ winner }) {
  const initial = (winner.userName || 'P').trim().slice(0, 1).toUpperCase() || 'P';
  return (
    <article className={`rr-podium-card rank-${winner.rank}${winner.isMe ? ' is-me' : ''}`}>
      <div className="rr-podium-rank">{winner.rank}</div>
      <div className="rr-podium-avatar-frame">
        <div className="rr-podium-crown">♛</div>
        <div
          className="rr-podium-avatar"
          style={winner.userAvatar ? { backgroundImage: `url("${winner.userAvatar}")` } : undefined}
        >
          {winner.userAvatar ? '' : initial}
        </div>
      </div>
      <div className="rr-podium-name">
        {winner.userName || 'Player'}
        {winner.isMe && <span className="rr-you-tag">You</span>}
      </div>
      <div className="rr-podium-payout">+💎 {formatNum(winner.payout)}</div>
    </article>
  );
}

function PodiumSkeleton() {
  return (
    <>
      {[2, 1, 3].map((rank) => (
        <article key={rank} className={`rr-podium-card rank-${rank} rr-skeleton`} aria-hidden="true">
          <div className="rr-podium-rank sk" />
          <div className="rr-podium-avatar-frame"><div className="rr-podium-avatar sk" /></div>
          <div className="rr-podium-name sk" />
          <div className="rr-podium-payout sk" />
        </article>
      ))}
    </>
  );
}

export default function RoundResultPanel({
  open,
  pending = false,
  backdrop = true,
  roundNumber,
  myResult,
  topWinners,
  onClose,
  children,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !myResult) return null;

  const { state, staked, payout, net, authoritative } = myResult;
  const celebrate = state === 'win' && !pending;

  return (
    <>
      {backdrop && <div className="rr-backdrop" onClick={onClose} />}
      {celebrate && <Confetti roundNumber={roundNumber} />}

      <div
        className={`rr-sheet${pending ? ' is-pending' : ''}`}
        role="dialog"
        aria-label="Round result"
        onClick={(e) => e.stopPropagation()}
      >
        {roundNumber != null && <div className="rr-round-tag">ROUND #{roundNumber}</div>}

        {children}

        <div className={`rr-your-result ${state}${pending ? ' pending' : ''}`}>
          {state === 'none' ? (
            <div className="rr-none-line">You didn't bet this round.</div>
          ) : (
            <>
              <div className="rr-result-headline">
                {state === 'win' ? '🎉 You won' : 'Better luck next round'}
              </div>
              <dl className="rr-result-figures">
                <div className="rr-figure">
                  <dt>Stake</dt>
                  <dd>💎 {formatNum(staked)}</dd>
                </div>
                <div className="rr-figure">
                  <dt>
                    Payout
                    {!authoritative && <span className="rr-est-chip">est.</span>}
                  </dt>
                  <dd>💎 {formatNum(payout)}</dd>
                </div>
                <div className="rr-figure rr-figure--net">
                  <dt>Net</dt>
                  <dd className={net > 0 ? 'up' : net < 0 ? 'down' : ''}>{formatSigned(net)}</dd>
                </div>
              </dl>
            </>
          )}
        </div>

        <div className="rr-winners">
          <div className="rr-winners-header">
            <h3>Top Winners</h3>
            <span className="rr-winners-count">
              {topWinners == null
                ? 'Tallying…'
                : topWinners.length > 0
                  ? `Top ${topWinners.length}`
                  : 'No winners'}
            </span>
          </div>
          <div className={`rr-podium${topWinners && topWinners.length ? ` count-${topWinners.length}` : ' is-empty'}`}>
            {topWinners == null ? (
              <PodiumSkeleton />
            ) : topWinners.length === 0 ? (
              <div className="rr-podium-empty">No winners this round — house keeps the pot.</div>
            ) : (
              topWinners.map((w) => <PodiumCard key={w.userId} winner={w} />)
            )}
          </div>
        </div>

        <button className="rr-continue" type="button" onClick={onClose}>Continue</button>
      </div>
    </>
  );
}
